import { useEffect, useState, useMemo, Fragment } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Loader2, Shield, ChevronDown, ChevronRight, Search, Lock, Download,
  Layers, History,
} from "lucide-react";
import { toast } from "sonner";
import PresetsDialog from "@/components/settings/PresetsDialog";
import { useClient } from "@/context/ClientContext";

const SCOPE_TONE = {
  module_visibility: "bg-violet-50 text-violet-700 border-violet-200",
  record_visibility: "bg-sky-50 text-sky-700 border-sky-200",
  record_action: "bg-emerald-50 text-emerald-700 border-emerald-200",
  workflow_action: "bg-amber-50 text-amber-700 border-amber-200",
  admin_action: "bg-rose-50 text-rose-700 border-rose-200",
  reporting_action: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

export default function PermissionMatrix() {
  const { activeSlug } = useClient();
  const [catalog, setCatalog] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [filter, setFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [collapsed, setCollapsed] = useState({});
  const [changes, setChanges] = useState([]);
  const [showChanges, setShowChanges] = useState(false);

  const load = () => {
    Promise.all([api.get("/permissions/catalog"), api.get("/permissions/matrix")]).then(
      ([c, m]) => { setCatalog(c.data); setMatrix(m.data); }
    );
  };
  useEffect(() => { load(); }, [activeSlug]);   // eslint-disable-line

  const restrictionKeys = useMemo(() => new Set(catalog?.restriction_keys || []), [catalog]);
  const scopeLabels = useMemo(() => {
    const out = {};
    (catalog?.scopes || []).forEach((s) => { out[s.key] = s.label; });
    return out;
  }, [catalog]);

  // Build Category -> [Group -> [actions]] honoring filter + scope
  const byCategory = useMemo(() => {
    if (!catalog) return [];
    const f = filter.trim().toLowerCase();
    const groupCat = catalog.group_category || {};
    const cats = {};
    catalog.actions.forEach((a) => {
      if (scopeFilter !== "all" && a.scope !== scopeFilter) return;
      if (f && !a.label.toLowerCase().includes(f) && !a.key.toLowerCase().includes(f)
          && !a.group.toLowerCase().includes(f) && !(a.category || "").toLowerCase().includes(f)) return;
      const cat = a.category || groupCat[a.group] || "Other";
      (cats[cat] = cats[cat] || {});
      (cats[cat][a.group] = cats[cat][a.group] || []).push(a);
    });
    return (catalog.categories || Object.keys(cats))
      .filter((c) => cats[c])
      .map((c) => {
        const groups = (catalog.groups || Object.keys(cats[c]))
          .filter((g) => cats[c][g])
          .map((g) => [g, cats[c][g]]);
        const total = groups.reduce((n, [, a]) => n + a.length, 0);
        return { category: c, groups, total };
      });
  }, [catalog, filter, scopeFilter]);

  const toggle = async (role, action, current) => {
    const key = `${role}:${action}`;
    setSavingKey(key);
    const next = !current;
    setMatrix((m) => ({ ...m, [role]: { ...m[role], [action]: next } }));
    try {
      await api.post("/permissions", { role, action, allowed: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      setMatrix((m) => ({ ...m, [role]: { ...m[role], [action]: current } }));
    } finally {
      setSavingKey(null);
    }
  };

  const toggleCat = (c) => setCollapsed((s) => ({ ...s, [c]: !s[c] }));

  const exportConfig = async () => {
    try {
      const { data } = await api.get("/permissions/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kippu-permissions-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Permission configuration exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const loadChanges = () => {
    setShowChanges((v) => !v);
    if (changes.length === 0) {
      api.get("/permissions/changes", { params: { limit: 50 } })
        .then((r) => setChanges(r.data || [])).catch(() => setChanges([]));
    }
  };

  if (!catalog) {
    return <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter permissions…" className="pl-9 h-9" data-testid="perm-filter-input" />
        </div>
        <Select value={scopeFilter} onValueChange={setScopeFilter}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="perm-scope-filter">
            <Layers className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
            <SelectValue placeholder="All scopes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scopes</SelectItem>
            {(catalog.scopes || []).map((s) => (
              <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <PresetsDialog catalog={catalog} matrix={matrix} onApplied={load} />
        <Button variant="outline" size="sm" className="h-9" onClick={exportConfig} data-testid="perm-export-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export
        </Button>
        <span className="text-xs text-slate-400 w-full sm:w-auto text-right">
          {catalog.actions.length} permissions · {catalog.roles.length} roles
        </span>
      </div>

      <Card className="border-slate-200" data-testid="permission-matrix">
        <CardContent className="p-0 overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold sticky left-0 top-0 z-30 bg-slate-50 shadow-[inset_0_-1px_0_0_#e2e8f0,inset_-1px_0_0_0_#e2e8f0]">
                  Action
                </th>
                {catalog.roles.map((r) => (
                  <th key={r} className="text-center px-3 py-3 text-xs font-semibold text-slate-700 whitespace-nowrap sticky top-0 z-20 bg-slate-50 shadow-[inset_0_-1px_0_0_#e2e8f0]">
                    <div className="flex flex-col items-center gap-0.5">
                      {r === "super_admin" && <Shield className="w-3.5 h-3.5 text-amber-500" />}
                      <span>{catalog.role_labels[r] || r}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byCategory.map(({ category, groups, total }) => {
                const catCollapsed = collapsed[category];
                return (
                  <Fragment key={`c-${category}`}>
                    <tr className="bg-slate-100/80 cursor-pointer hover:bg-slate-200/60"
                      onClick={() => toggleCat(category)}
                      data-testid={`perm-category-${category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      <td colSpan={catalog.roles.length + 1}
                        className="px-3 py-2.5 text-xs uppercase tracking-wider text-slate-800 font-bold">
                        <span className="sticky left-0 inline-flex items-center gap-1.5 bg-slate-100 pr-3 py-0.5 rounded-sm">
                          {catCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {category}
                          <span className="text-slate-400 font-normal normal-case">({total})</span>
                        </span>
                      </td>
                    </tr>
                    {!catCollapsed && groups.map(([group, actions]) => (
                      <Fragment key={`g-${group}`}>
                        <tr className="bg-slate-50/70">
                          <td colSpan={catalog.roles.length + 1}
                            className="px-8 py-1.5 text-[11px] uppercase tracking-wider text-indigo-600 font-semibold">
                            <span className="sticky left-0 inline-block bg-slate-50 pr-3">{group}</span>
                          </td>
                        </tr>
                        {actions.map((a) => {
                          const isRestriction = restrictionKeys.has(a.key);
                          return (
                            <tr key={a.key} className="border-b border-slate-100 hover:bg-slate-50/60"
                              data-testid={`perm-row-${a.key}`}>
                              <td className="px-4 py-2.5 text-slate-700 sticky left-0 z-10 bg-white shadow-[inset_-1px_0_0_0_#e2e8f0]">
                                <div className="text-sm flex items-center gap-1.5 flex-wrap">
                                  {a.label}
                                  <Badge variant="outline"
                                    className={`text-[9px] ${SCOPE_TONE[a.scope] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
                                    {scopeLabels[a.scope] || a.scope}
                                  </Badge>
                                  {isRestriction && (
                                    <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-600 border-rose-200 gap-1">
                                      <Lock className="w-2.5 h-2.5" /> restriction
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-[10px] text-slate-400 font-mono">{a.key}</div>
                              </td>
                              {catalog.roles.map((r) => {
                                const allowed = !!matrix[r]?.[a.key];
                                const isSaving = savingKey === `${r}:${a.key}`;
                                const isSuper = r === "super_admin";
                                return (
                                  <td key={r} className="text-center px-3 py-2.5">
                                    {isSuper ? (
                                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">always</Badge>
                                    ) : (
                                      <div className="inline-flex items-center justify-center">
                                        {isSaving ? (
                                          <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                                        ) : (
                                          <Checkbox checked={allowed}
                                            onCheckedChange={() => toggle(r, a.key, allowed)}
                                            data-testid={`perm-${r}-${a.key}`} />
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Audit & Compliance — recent permission changes */}
      <div>
        <Button variant="ghost" size="sm" onClick={loadChanges} data-testid="perm-changes-toggle"
          className="text-slate-600">
          <History className="w-3.5 h-3.5 mr-1.5" />
          {showChanges ? "Hide" : "Show"} recent permission changes
        </Button>
        {showChanges && (
          <Card className="border-slate-200 mt-2" data-testid="perm-changes-panel">
            <CardContent className="p-0 max-h-72 overflow-y-auto">
              {changes.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">No recent permission changes.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {changes.map((c, i) => (
                    <li key={i} className="px-4 py-2 text-xs flex items-center gap-3">
                      <span className="font-mono text-slate-400 w-36 shrink-0">
                        {c.created_at ? new Date(c.created_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </span>
                      <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 shrink-0">
                        {(c.action || "").replace(/_/g, " ")}
                      </Badge>
                      <span className="text-slate-600 truncate">
                        {c.user_name || c.user_email || "system"}
                        {c.entity_id ? <span className="text-slate-400"> · {c.entity_id}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
